<template>
  <div class="page-container">
    <div class="page-content">
    <!-- Header -->
    <div class="mb-6">
      <div class="flex justify-between items-center">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">{{ t('inventory.title') }}</h1>
          <p class="text-gray-600">{{ t('inventory.subtitle') }}</p>
        </div>
        <router-link to="/inventory/movement" class="btn btn-primary">
          {{ t('inventory.registerMovement') }}
        </router-link>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="error" class="alert alert-error mb-4 flex justify-between items-center">
      <span>{{ error }}</span>
      <button @click="inventoryStore.clearError()" class="btn btn-sm btn-ghost">&times;</button>
    </div>

    <!-- Tabs -->
    <div class="border-b border-gray-200 mb-6">
      <nav class="-mb-px flex space-x-8">
        <button
          @click="activeTab = 'stock'"
          class="tab-btn"
          :class="activeTab === 'stock' ? 'tab-btn-active' : 'tab-btn-inactive'"
        >
          {{ t('inventory.tabStock') }}
        </button>
        <button
          @click="activeTab = 'history'"
          class="tab-btn"
          :class="activeTab === 'history' ? 'tab-btn-active' : 'tab-btn-inactive'"
        >
          {{ t('inventory.tabHistory') }}
        </button>
      </nav>
    </div>

    <!-- Tab: Stock -->
    <div v-if="activeTab === 'stock'">
      <!-- Filters -->
      <div class="flex gap-4 mb-4">
        <input
          v-model="stockSearch"
          type="text"
          :placeholder="t('inventory.searchStockPlaceholder')"
          class="input flex-1"
        />
        <select v-model="selectedWarehouseId" @change="onWarehouseChange" class="select w-56">
          <option value="">{{ t('inventory.warehouseAll') }}</option>
          <option v-for="wh in activeWarehouses" :key="wh.id" :value="wh.id">
            {{ wh.name }}
          </option>
        </select>
      </div>

      <!-- Summary -->
      <div class="mb-4 text-sm text-gray-600">
        <span>{{ visibleStockItems.length }} {{ t('inventory.products') }}</span>
        <span v-if="inventoryStore.itemsOutOfStock.length > 0" class="ml-3 text-red-600 font-medium">
          {{ inventoryStore.itemsOutOfStock.length }} {{ t('inventory.outOfStock') }}
        </span>
        <span v-if="inventoryStore.itemsLowStock.length > 0" class="ml-3 text-yellow-600 font-medium">
          {{ inventoryStore.itemsLowStock.length }} {{ t('inventory.lowStock') }}
        </span>
      </div>

      <!-- Stock Table -->
      <div class="card">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="table-th">{{ t('inventory.colProduct') }}</th>
                <th class="table-th">{{ t('inventory.colSku') }}</th>
                <th class="table-th text-right">{{ t('inventory.colAvailable') }}</th>
                <th class="table-th text-right">{{ t('inventory.colReserved') }}</th>
                <th class="table-th text-center">{{ t('inventory.colStatus') }}</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="loading && inventoryStore.inventoryItems.length === 0">
                <td colspan="5" class="px-6 py-8 text-center text-gray-500">
                  {{ t('inventory.loadingInventory') }}
                </td>
              </tr>
              <tr v-else-if="visibleStockItems.length === 0">
                <td colspan="5" class="px-6 py-8 text-center text-gray-500">
                  {{ t('inventory.noProducts') }}
                </td>
              </tr>
              <tr v-for="item in visibleStockItems" :key="item.productId" class="hover:bg-gray-50">
                <td class="px-6 py-4 text-sm font-medium text-gray-900">{{ item.productName }}</td>
                <td class="px-6 py-4 text-sm text-gray-500">{{ item.productSku }}</td>
                <td class="px-6 py-4 text-sm text-gray-900 text-right">
                  {{ item.availableStock }} {{ item.unit }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-500 text-right">
                  {{ item.reservedStock }} {{ item.unit }}
                </td>
                <td class="px-6 py-4 text-center">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="stockStatusClass(item.stockStatus)">
                    {{ stockStatusLabel(item.stockStatus) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Tab: History -->
    <div v-if="activeTab === 'history'">
      <!-- Filters -->
      <div class="flex gap-4 mb-4 flex-wrap">
        <select v-model="movementTypeFilter" class="select w-44">
          <option value="ALL">{{ t('inventory.typeAll') }}</option>
          <option value="IN">{{ t('movements.stockIn') }}</option>
          <option value="OUT">{{ t('movements.stockOut') }}</option>
          <option value="TRANSFER">{{ t('movements.transfer') }}</option>
          <option value="ADJUSTMENT">{{ t('movements.adjustment') }}</option>
        </select>
        <input
          v-model="movementSearch"
          type="text"
          :placeholder="t('inventory.searchMovementPlaceholder')"
          class="input flex-1"
        />
        <input
          v-model="startDateStr"
          type="date"
          class="input w-40"
          :max="endDateStr || undefined"
        />
        <input
          v-model="endDateStr"
          type="date"
          class="input w-40"
          :min="startDateStr || undefined"
        />
      </div>

      <!-- Movements Table -->
      <div class="card">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="table-th">{{ t('inventory.colDate') }}</th>
                <th class="table-th">{{ t('inventory.colType') }}</th>
                <th class="table-th">{{ t('inventory.colProduct') }}</th>
                <th class="table-th text-right">{{ t('inventory.colQty') }}</th>
                <th class="table-th">{{ t('inventory.colReference') }}</th>
                <th class="table-th">{{ t('inventory.colStatus') }}</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="loading && inventoryStore.movements.length === 0">
                <td colspan="6" class="px-6 py-8 text-center text-gray-500">
                  {{ t('inventory.loadingMovements') }}
                </td>
              </tr>
              <tr v-else-if="visibleMovements.length === 0">
                <td colspan="6" class="px-6 py-8 text-center text-gray-500">
                  {{ t('inventory.noMovements') }}
                </td>
              </tr>
              <tr v-for="mov in visibleMovements" :key="mov.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                  {{ formatDate(mov.createdAt) }}
                </td>
                <td class="px-6 py-4 text-sm whitespace-nowrap">
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" :class="movementTypeClass(mov.movementType)">
                    {{ movementTypeIcon(mov.movementType) }} {{ movementTypeLabel(mov.movementType) }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm text-gray-900">
                  <div>{{ mov.productName }}</div>
                  <div class="text-xs text-gray-400">{{ mov.productSku }}</div>
                </td>
                <td class="px-6 py-4 text-sm text-gray-900 text-right whitespace-nowrap">
                  {{ mov.quantity }} {{ mov.unit }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-500">
                  <span v-if="mov.reference">{{ mov.reference.type }}-{{ mov.reference.number }}</span>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <td class="px-6 py-4 text-sm">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {{ mov.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useInventoryStore } from '../../stores/useInventoryStore';
import { useWarehouseStore } from '../../stores/useWarehouseStore';
import type { StockStatus } from '../../../packages/core/application/dtos/inventory/InventoryItemDTO';

const { t } = useI18n();
const inventoryStore = useInventoryStore();
const warehouseStore = useWarehouseStore();

const activeTab = ref<'stock' | 'history'>('stock');
const stockSearch = ref('');
const selectedWarehouseId = ref('');
const movementTypeFilter = ref('ALL');
const movementSearch = ref('');
const startDateStr = ref('');
const endDateStr = ref('');

const loading = computed(() => inventoryStore.loading);
const error = computed(() => inventoryStore.error);
const activeWarehouses = computed(() => warehouseStore.activeWarehouses);

const visibleStockItems = computed(() =>
  inventoryStore.filteredItems(stockSearch.value, selectedWarehouseId.value || undefined)
);

const visibleMovements = computed(() => {
  const startDate = startDateStr.value ? new Date(startDateStr.value) : undefined;
  const endDate = endDateStr.value ? new Date(endDateStr.value + 'T23:59:59') : undefined;

  return inventoryStore.filteredMovements(movementSearch.value, movementTypeFilter.value).filter((m) => {
    if (startDate && new Date(m.createdAt) < startDate) return false;
    if (endDate && new Date(m.createdAt) > endDate) return false;
    return true;
  });
});

const onWarehouseChange = () => {
  inventoryStore.fetchInventorySnapshot(
    selectedWarehouseId.value ? { warehouseId: selectedWarehouseId.value } : undefined
  );
};

const formatDate = (date: Date) =>
  new Date(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });

const stockStatusClass = (status: StockStatus) => {
  switch (status) {
    case 'NORMAL': return 'bg-green-100 text-green-800';
    case 'LOW_STOCK': return 'bg-yellow-100 text-yellow-800';
    case 'OUT_OF_STOCK': return 'bg-red-100 text-red-800';
  }
};

const stockStatusLabel = (status: StockStatus) => {
  switch (status) {
    case 'NORMAL': return t('inventory.stockOk');
    case 'LOW_STOCK': return t('inventory.stockLow');
    case 'OUT_OF_STOCK': return t('inventory.stockOut');
  }
};

const movementTypeLabel = (type: string) => {
  switch (type) {
    case 'IN': return t('movements.stockIn');
    case 'OUT': return t('movements.stockOut');
    case 'TRANSFER': return t('movements.transfer');
    case 'ADJUSTMENT': return t('movements.adjustment');
    default: return type;
  }
};

const movementTypeIcon = (type: string) => {
  switch (type) {
    case 'IN': return '↑';
    case 'OUT': return '↓';
    case 'TRANSFER': return '↔';
    case 'ADJUSTMENT': return '⚡';
    default: return '';
  }
};

const movementTypeClass = (type: string) => {
  switch (type) {
    case 'IN': return 'bg-green-100 text-green-800';
    case 'OUT': return 'bg-red-100 text-red-800';
    case 'TRANSFER': return 'bg-blue-100 text-blue-800';
    case 'ADJUSTMENT': return 'bg-purple-100 text-purple-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

onMounted(async () => {
  await Promise.all([
    inventoryStore.fetchInventorySnapshot(),
    inventoryStore.fetchMovements(),
    warehouseStore.fetchWarehouses(),
  ]);
});
</script>

<style scoped>
.tab-btn {
  @apply py-4 px-1 border-b-2 font-medium text-sm;
}
.tab-btn-active {
  @apply border-indigo-500 text-indigo-600;
}
.tab-btn-inactive {
  @apply border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300;
}
.table-th {
  @apply px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider;
}
</style>
